<template>
  <div class="activity-import-columns">
    <button
      type="button"
      class="activity-import-columns__toggle"
      :class="{ 'is-open': expanded }"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="expanded = !expanded"
    >
      <span class="activity-import-columns__toggle-label">{{ t.title }} ({{ selectedCount }})</span>
      <span class="activity-import-columns__chevron" :class="{ 'is-open': expanded }" aria-hidden="true">▾</span>
    </button>

    <div v-show="expanded" class="activity-import-columns__body">
      <div class="activity-import-columns__actions">
        <button
          type="button"
          class="button button--white button--light button--stroke button--square activity-import-columns__action"
          @click="setAll(true)"
        >{{ t.select_all }}</button>
        <button
          type="button"
          class="button button--white button--light button--stroke button--square activity-import-columns__action"
          @click="setAll(false)"
        >{{ t.unselect_all }}</button>
      </div>

      <table class="activity-import-columns__table">
        <colgroup>
          <col class="activity-import-columns__col--include" />
          <col class="activity-import-columns__col--source" />
          <col class="activity-import-columns__col--field" />
        </colgroup>
        <thead>
          <tr>
            <th>{{ t.include }}</th>
            <th>{{ t.imported_column }}</th>
            <th>{{ t.bobcat_field }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(field, index) in rows" :key="field.id" :class="{ 'is-excluded': !field.included }">
            <td class="activity-import-columns__include-cell">
              <input type="hidden" :name="fieldName(index, 'id')" :value="field.id" />
              <input type="hidden" :name="fieldName(index, 'included')" value="0" />
              <label class="switch">
                <input
                  type="checkbox"
                  class="switch__el"
                  value="1"
                  v-model="field.included"
                  :name="fieldName(index, 'included')"
                />
                <span class="switch__holder"><span class="switch__button"></span></span>
              </label>
            </td>
            <td class="activity-import-columns__source">{{ field.source_header }}</td>
            <td class="activity-import-columns__field">
              <template v-if="field.status === 'matched'">
                <span class="activity-import-columns__mapped">{{ field.mapped_header }}</span>
                <input type="hidden" :name="fieldName(index, 'mapped_key')" :value="field.mapped_key" />
              </template>
              <select
                v-else
                class="select select--full"
                v-model="field.mapped_key"
                :name="fieldName(index, 'mapped_key')"
              >
                <option :value="null">{{ t.do_not_import }}</option>
                <option v-for="option in options" :key="option.key" :value="option.key">
                  {{ option.header }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="selectedCount === 0" class="activity-import-columns__empty" role="alert">
        {{ t.none_selected }}
      </p>
    </div>

    <!-- Live, schema-driven Preview: a fixed Action column plus up to three of the
         organizer's currently included columns (chosen by preview_priority, then
         imported order), rendered per each column's preview_display. -->
    <section v-if="previewColumns.length" class="activity-import-preview">
      <div class="section-heading-group">
        <h4 class="section-heading"><span class="section-heading__text">{{ preview.title }}</span></h4>
      </div>

      <table class="activity-import-preview__table">
        <colgroup>
          <col class="activity-import-preview__col--action" />
          <col v-for="col in previewColumns" :key="`c-${col.field.id}`" :style="{ width: dataColWidth }" />
        </colgroup>
        <thead>
          <tr>
            <th>{{ preview.action }}</th>
            <th v-for="col in previewColumns" :key="`h-${col.field.id}`">{{ col.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, r) in previewCellRows" :key="r">
            <td class="activity-import-preview__action" v-html="actionIconFor(r)"></td>
            <td v-for="col in previewColumns" :key="`d-${col.field.id}`" class="activity-import-preview__cell">
              <span :class="cellClass(col.display)" :title="row[col.index] || ''">{{ row[col.index] }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="activity-import-preview__legend">
        <span v-for="action in ['create', 'update', 'error']" :key="action" class="activity-import-preview__legend-item">
          <span v-html="icons[action]"></span>
          <span class="activity-import-preview__legend-label">{{ (preview.legend || {})[action] }}</span>
        </span>
      </div>

      <p class="activity-import-preview__note">{{ preview.note }}</p>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    fields: { type: String, default: '[]' },
    fieldOptions: { type: String, default: '[]' },
    labels: { type: String, default: '{}' },
    previewCells: { type: String, default: '[]' },
    previewActions: { type: String, default: '[]' },
    actionIcons: { type: String, default: '{}' },
    // CSS selector of the footer Continue button this component enables/disables.
    submitSelector: { type: String, default: '' },
  },

  data() {
    return { rows: [], expanded: false };
  },

  created() {
    this.rows = this.safeParse(this.fields, []);
  },

  computed: {
    options() {
      return this.safeParse(this.fieldOptions, []);
    },

    t() {
      return this.safeParse(this.labels, {});
    },

    preview() {
      return this.t.preview || {};
    },

    icons() {
      return this.safeParse(this.actionIcons, {});
    },

    previewCellRows() {
      return this.safeParse(this.previewCells, []);
    },

    previewActionList() {
      return this.safeParse(this.previewActions, []);
    },

    // { columnKey: { priority, label, display } } from the schema-driven options.
    previewMetaByKey() {
      const map = {};
      this.options.forEach((option) => {
        map[option.key] = {
          priority: option.preview_priority || 0,
          label: option.preview_label,
          display: option.preview_display || 'single_line',
        };
      });
      return map;
    },

    // The up-to-three data columns to preview: the currently included + mapped
    // columns, highest preview_priority first, ties in imported (field) order.
    // `index` is the field's position among rows, which aligns with previewCells.
    previewColumns() {
      const meta = this.previewMetaByKey;
      const included = this.rows
        .map((field, index) => ({ field, index }))
        .filter(({ field }) => field.included && field.mapped_key);
      included.sort((a, b) => {
        const pa = (meta[a.field.mapped_key] || {}).priority || 0;
        const pb = (meta[b.field.mapped_key] || {}).priority || 0;
        return pb - pa || a.index - b.index;
      });
      return included.slice(0, 3).map(({ field, index }) => {
        const m = meta[field.mapped_key] || {};
        return {
          field,
          index,
          label: m.label || field.mapped_header || field.source_header,
          display: m.display || 'single_line',
        };
      });
    },

    dataColWidth() {
      return `${Math.floor(86 / (this.previewColumns.length || 1))}%`;
    },

    // The count reflects columns currently switched on for import, not the total
    // detected -- it updates live as switches and Select/Unselect All change.
    selectedCount() {
      return this.rows.filter((field) => field.included).length;
    },

    // Never let the organizer advance with a no-op import.
    canContinue() {
      return this.selectedCount > 0;
    },
  },

  watch: {
    canContinue: {
      immediate: true,
      handler(value) {
        this.$nextTick(() => this.toggleContinue(value));
      },
    },
  },

  methods: {
    safeParse(value, fallback) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return fallback;
      }
    },

    fieldName(index, attribute) {
      return `activity_import[fields_attributes][${index}][${attribute}]`;
    },

    toggleContinue(enabled) {
      if (!this.submitSelector) return;
      const button = document.querySelector(this.submitSelector);
      if (button) button.disabled = !enabled;
    },

    setAll(value) {
      this.rows.forEach((field) => {
        field.included = value;
      });
    },

    // Free text / URLs clamp to two lines; everything else stays on one line.
    cellClass(display) {
      return display === 'two_line' || display === 'url'
        ? 'activity-import-preview__cell--clamp'
        : 'activity-import-preview__cell--nowrap';
    },

    actionIconFor(rowIndex) {
      return this.icons[this.previewActionList[rowIndex]] || '';
    },
  },
};
</script>
